import type {
  FieldValue,
  OntologyField,
  TicketFieldValues,
} from './ontologyFieldTypes'
import {
  COMPONENT_SUBTREE_KEY,
  getComponentSubtree,
  readComponentSubtree,
} from './ontologyFieldTypes'

/**
 * Pure helpers for walking a workflow's ontology fields and a ticket's fieldValues across
 * both scopes (top-level "ticket" and nested "component" under
 * `ontologyFields.components` / `fieldValues.components`).
 *
 * These are React-free so they can be used from pure modules (e.g.,
 * `dashboard/ticketLlmStats/computeTicketLlmStats.ts`) as well as from hook wrappers
 * (`useFlattenedOntologyFields`).
 */

/**
 * A workflow's ontology definition knows whether a field is `'ticket'`- or `'component'`-scoped,
 * but does NOT know concrete component ids — those exist only in tickets or unit data.
 */
export interface FlattenedOntologyDefinition {
  scope: 'ticket' | 'component'
  fieldId: string
  definition: OntologyField
}

/**
 * A ticket's flattened field value carries a concrete `componentId` when scope is component.
 */
export interface FlattenedTicketFieldValue {
  scope: 'ticket' | { kind: 'component'; componentId: string }
  fieldId: string
  fieldName: string | undefined
  value: FieldValue
}

const isPlainRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)

const isFieldValueShape = (v: unknown): v is FieldValue =>
  isPlainRecord(v) && 'value' in v

/**
 * Split a workflow's `ontologyFields` into ticket-scoped and component-scoped definitions.
 * Skips the reserved `components` key from the ticket-scoped enumeration; walks it into
 * component-scoped entries.
 *
 * Accepts `unknown` (workflow globalContext is loosely typed) and narrows internally.
 */
export function flattenWorkflowOntologyFields(
  ontologyFields: unknown
): FlattenedOntologyDefinition[] {
  if (!isPlainRecord(ontologyFields)) return []
  const out: FlattenedOntologyDefinition[] = []
  for (const [fieldId, value] of Object.entries(ontologyFields)) {
    if (fieldId === COMPONENT_SUBTREE_KEY) {
      if (!isPlainRecord(value)) continue
      for (const [componentFieldId, def] of Object.entries(value)) {
        out.push({
          scope: 'component',
          fieldId: componentFieldId,
          definition: def as OntologyField,
        })
      }
      continue
    }
    out.push({
      scope: 'ticket',
      fieldId,
      definition: value as OntologyField,
    })
  }
  return out
}

/**
 * Walk a ticket's `fieldValues` across both scopes. Joins in field display names from the
 * workflow's `ontologyFields` (top-level lookup for ticket-scoped rows; `.components[...]`
 * lookup for component-scoped rows). `fieldName` is `undefined` when the ontology definition
 * is missing (dev fixtures, orphaned fields).
 */
export function flattenTicketFieldValues(
  fieldValues: TicketFieldValues | undefined,
  ontologyFields: unknown
): FlattenedTicketFieldValue[] {
  if (!fieldValues) return []
  const ontologyRecord = isPlainRecord(ontologyFields) ? ontologyFields : {}
  const rawComponentOntology = readComponentSubtree(ontologyRecord)
  const componentOntology = isPlainRecord(rawComponentOntology)
    ? (rawComponentOntology as Record<string, OntologyField>)
    : {}

  const nameOf = (
    map: Record<string, unknown>,
    fieldId: string
  ): string | undefined => {
    const def = map[fieldId]
    if (isPlainRecord(def) && typeof def.name === 'string') {
      return def.name
    }
    return undefined
  }

  const out: FlattenedTicketFieldValue[] = []
  // Component-scoped rows first (from the reserved `components` subtree).
  const components = getComponentSubtree(fieldValues)
  if (components) {
    for (const [componentId, byField] of Object.entries(components)) {
      for (const [componentFieldId, fv] of Object.entries(byField)) {
        if (!isFieldValueShape(fv)) continue
        out.push({
          scope: { kind: 'component', componentId },
          fieldId: componentFieldId,
          fieldName: nameOf(componentOntology, componentFieldId),
          value: fv,
        })
      }
    }
  }
  // Ticket-scoped rows (top-level slots, skipping the reserved key).
  for (const [fieldId, value] of Object.entries(fieldValues)) {
    if (fieldId === COMPONENT_SUBTREE_KEY) continue
    if (!isFieldValueShape(value)) continue
    out.push({
      scope: 'ticket',
      fieldId,
      fieldName: nameOf(ontologyRecord, fieldId),
      value,
    })
  }
  return out
}
